// Zone count: auto-picked by an explained-variance elbow (each added zone must explain ≥8% more of
// the field's covariate variation), user-overridable. Deterministic per (field, k): seeded
// k-means++.
import { polygonAreaM2, pointInPolygons, polygonsOf } from "./samplingDesign";
import { ringCentroid } from "./cellCentroid";
import { splitToCount, mergeToCount } from "./zoneSplit";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

export interface ZoneDesignArgs {
  // cell polygons (lon/lat GeoJSON geometries), aligned by index with `features` rows
  geoms: { type: string; coordinates: unknown }[];
  features: (number | null)[][]; // per-COLUMN value arrays
  weights?: (number | null)[]; // per-COLUMN Mahalanobis weight (aligned with `features`); absent/≤0 → 1 (equal)
  k?: number | null; // override; null/absent = auto-pick
  // PURE-CATEGORICAL base only (zoneEditStore steering): classes are identities, so every label's
  // last cell/patch survives the cleanup even under the floor. Mixed designs must NOT set this —
  // they get the probe-and-exact path instead.
  protectK?: boolean;
  // the field's STORED boundary acres (`DashboardField.acres` = `field_boundaries.area_acres`) for the
  // auto-pick's acreage term — never a computed area (the three-figures lesson); absent → cell-sum fallback
  boundaryAcres?: number;
  seed: number;
  minZoneAcres?: number; // fragments smaller than this merge into their surroundings
}

export interface ZoneDesignResult {
  fc: GeoJSON.FeatureCollection; // one feature per zone: {zone, area_acres}, MultiPolygon
  k: number; // zones actually produced (post-merge)
  requestedK: number; // the count asked of the clustering (override or autoK, clamped)
  autoK: number;
  autoDeliveredK?: number;
  evByK: Record<number, number>; // explained covariate variance for each candidate k
  ev: number; // for the k actually used
  labelPoints: { zone: number; lng: number; lat: number }[]; // one label anchor per zone
  labels: number[]; // per-cell zone id (1-based, area-ordered), index-aligned with the input geoms — for the editor
}

const K_MIN = 2;
const K_MAX = 6; // the explained-variance ELBOW scan range (the "homogeneity" auto pick)
// A zone may be MULTI-PART (the same soil type / landscape position recurring across a field), but
// each PART must still be farmable.
export const MIN_ZONE_PART_ACRES = 1;
// 8% → 4%: the elbow now leans toward more, finer zones on structured fields — expect autoK to hit
// the K_MAX=6 cap more often.
const MIN_EV_GAIN = 0.04;
// The auto-pick's ACREAGE TERM: the recommendation keeps growing until the elbow gain is under 4%
// AND the average zone would drop under this many acres — autoK = the GREATER of the elbow's pick
// and floor(acres / 10), capped at MAX_ZONES.
const TARGET_ACRES_PER_ZONE = 10;
// The 4-acre-floor "suggested" count can far exceed the elbow range on a large field (a 160-ac
// field ⇒ ~40 four-acre zones), so the design must honor a requested k well past K_MAX; the elbow
// auto-pick stays capped at K_MAX.
export const MAX_ZONES = 40;
// how far above an explicit k the probe sweep re-runs the original process before falling back to the
// exactness patch (merge-down / split-up). Bounded for cost — each probe is a full design pass.
const PROBE_SPAN = 8;

// ---- small deterministic PRNG (same construction as samplingDesign) --------------------
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- feature matrix + Mahalanobis whitening ---------------------------------------------
// standardize usable columns, ridge-regularize the covariance, Cholesky-whiten:
// distance in the whitened space IS Mahalanobis distance in the original space.
export function whitenFeatures(
  features: (number | null)[][],
  n: number,
  weights?: (number | null)[],
): number[][] | null {
  const cols: number[][] = [];
  const w: number[] = []; // per-USABLE-column weight, aligned with `cols` after the drop filters
  features.forEach((col, ci) => {
    if (col.length !== n) return;
    const present = col.filter(
      (v): v is number => v != null && Number.isFinite(v),
    );
    if (present.length < n / 2) return;
    const m = present.reduce((s, v) => s + v, 0) / present.length;
    const sd = Math.sqrt(
      present.reduce((s, v) => s + (v - m) ** 2, 0) / present.length,
    );
    if (!(sd > 0)) return;
    cols.push(
      col.map((v) => (v == null || !Number.isFinite(v) ? 0 : (v - m) / sd)),
    );
    const wi = weights?.[ci];
    w.push(wi != null && Number.isFinite(wi) && wi > 0 ? wi : 1);
  });
  const d = cols.length;
  if (!d) return null;

  // covariance of the (UNWEIGHTED) standardized columns (diagonal ≈ 1), ridge for near-duplicates. The
  // covariance/Cholesky are computed WITHOUT the user weights so the whitening basis stays fixed — the
  // weights are applied to the whitened INPUT below (weighted Mahalanobis: d² = Δzᵀ·diag(w)·Σ⁻¹·diag(w)·Δz).
  // Weighting the columns here instead would let the covariance normalize the weight right back out.
  const cov: number[][] = Array.from({ length: d }, () =>
    new Array<number>(d).fill(0),
  );
  for (let a = 0; a < d; a++) {
    for (let b = a; b < d; b++) {
      let s = 0;
      for (let i = 0; i < n; i++) s += cols[a][i] * cols[b][i];
      cov[a][b] = cov[b][a] = s / n + (a === b ? 1e-3 : 0);
    }
  }
  // Cholesky cov = L·Lᵀ
  const L: number[][] = Array.from({ length: d }, () =>
    new Array<number>(d).fill(0),
  );
  for (let a = 0; a < d; a++) {
    for (let b = 0; b <= a; b++) {
      let s = cov[a][b];
      for (let c = 0; c < b; c++) s -= L[a][c] * L[b][c];
      if (a === b) {
        L[a][a] = Math.sqrt(Math.max(s, 1e-9));
      } else {
        L[a][b] = s / L[b][b];
      }
    }
  }
  // whiten each row: solve L·y = (w ⊙ x) (forward substitution). At w ≡ 1 this is the plain Mahalanobis
  // whitening; a heavier weight scales that column's standardized deviation so it dominates the distance.
  const X: number[][] = new Array(n);
  for (let i = 0; i < n; i++) {
    const y = new Array<number>(d);
    for (let a = 0; a < d; a++) {
      let s = w[a] * cols[a][i];
      for (let c = 0; c < a; c++) s -= L[a][c] * y[c];
      y[a] = s / L[a][a];
    }
    X[i] = y;
  }
  return X;
}

// ---- seeded k-means (k-means++ init, Lloyd) --------------------------------------------
function kmeans(
  X: number[][],
  k: number,
  seed: number,
): { assign: number[]; withinSS: number } {
  const n = X.length;
  const d = X[0].length;
  const rng = mulberry32(seed);
  const d2 = (p: number[], q: number[]) => {
    let s = 0;
    for (let i = 0; i < d; i++) s += (p[i] - q[i]) ** 2;
    return s;
  };
  const centroids: number[][] = [X[Math.floor(rng() * n)].slice()];
  const minD = new Array<number>(n).fill(Infinity);
  while (centroids.length < k) {
    const last = centroids[centroids.length - 1];
    let total = 0;
    for (let i = 0; i < n; i++) {
      minD[i] = Math.min(minD[i], d2(X[i], last));
      total += minD[i];
    }
    let target = rng() * total;
    let pick = n - 1;
    for (let i = 0; i < n; i++) {
      target -= minD[i];
      if (target <= 0) {
        pick = i;
        break;
      }
    }
    centroids.push(X[pick].slice());
  }
  const assign = new Array<number>(n).fill(0);
  for (let iter = 0; iter < 50; iter++) {
    let changed = false;
    for (let i = 0; i < n; i++) {
      let best = 0;
      let bestD = Infinity;
      for (let c = 0; c < k; c++) {
        const dd = d2(X[i], centroids[c]);
        if (dd < bestD) {
          bestD = dd;
          best = c;
        }
      }
      if (assign[i] !== best) {
        assign[i] = best;
        changed = true;
      }
    }
    const sums = centroids.map(() => new Array<number>(d).fill(0));
    const counts = new Array<number>(k).fill(0);
    for (let i = 0; i < n; i++) {
      counts[assign[i]]++;
      for (let a = 0; a < d; a++) sums[assign[i]][a] += X[i][a];
    }
    let reseeded = false;
    for (let c = 0; c < k; c++) {
      if (counts[c] === 0) {
        let far = 0;
        let farD = -1;
        for (let i = 0; i < n; i++) {
          const dd = d2(X[i], centroids[assign[i]]);
          if (dd > farD) {
            farD = dd;
            far = i;
          }
        }
        centroids[c] = X[far].slice();
        reseeded = true;
      } else {
        for (let a = 0; a < d; a++) centroids[c][a] = sums[c][a] / counts[c];
      }
    }
    // Converged once assignments are stable. An empty-cluster reseed gets a few sweeps to
    // attract points; but if assignments STILL don't move it means k exceeds the number of
    // fillable clusters, so we stop rather than reseed the same empties for all 50 iterations
    // (the high-k main-thread grind the review flagged). Normal k (no reseed) breaks at once.
    if (!changed && (!reseeded || iter >= 8)) break;
  }
  let withinSS = 0;
  for (let i = 0; i < n; i++) withinSS += d2(X[i], centroids[assign[i]]);
  return { assign, withinSS };
}

// ---- geometry helpers -------------------------------------------------------------------
export type Ring = number[][];
function normalizeRing(ring: Ring): Ring {
  // drop the closing duplicate vertex if present
  const r =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring.slice();
  // normalize to CCW so shared edges between neighbors run in OPPOSITE directions
  let area = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++)
    area += r[j][0] * r[i][1] - r[i][0] * r[j][1];
  if (area < 0) r.reverse();
  return r;
}
// ALL outer rings of a cell (a boundary-split cell can be a MultiPolygon — every part
// must participate in the dissolve or its area silently drops out of the zone map)
export function outerRingsOf(geom: {
  type: string;
  coordinates: unknown;
}): Ring[] {
  if (geom.type === "MultiPolygon")
    return (geom.coordinates as Ring[][]).map((poly) =>
      normalizeRing((poly?.[0] ?? []) as Ring),
    );
  return [normalizeRing(((geom.coordinates as Ring[])?.[0] ?? []) as Ring)];
}

export function centroidOfRing(r: Ring): { lng: number; lat: number } {
  return ringCentroid(r); // true center — drops the closing duplicate (square corner-bias fix; see cellCentroid.ts)
}

// Shared-edge adjacency: two cells are neighbors iff they share an edge — rook-4
// for the 8 m square grid, and 6 for the legacy cell grid (a cell shares 6 edges).
export function buildAdjacency(ringsPerCell: Ring[][]): number[][] {
  const n = ringsPerCell.length;
  const vk = (p: number[]) => `${p[0].toFixed(9)},${p[1].toFixed(9)}`;
  const edgeToCells = new Map<string, number[]>(); // undirected edge key → cells touching it
  for (let i = 0; i < n; i++) {
    for (const r of ringsPerCell[i]) {
      const m = r.length;
      for (let j = 0; j < m; j++) {
        const a = r[j];
        const b = r[(j + 1) % m];
        if (a[0] === b[0] && a[1] === b[1]) continue; // skip a degenerate zero-length edge
        const ka = vk(a);
        const kb = vk(b);
        const ek = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`; // canonical (direction-independent)
        const arr = edgeToCells.get(ek);
        if (arr) {
          if (arr[arr.length - 1] !== i) arr.push(i);
        } // one cell touches an edge once
        else edgeToCells.set(ek, [i]);
      }
    }
  }
  const sets: Set<number>[] = Array.from(
    { length: n },
    () => new Set<number>(),
  );
  for (const cells of edgeToCells.values()) {
    if (cells.length < 2) continue; // boundary edge (only one cell) → no neighbor
    for (let x = 0; x < cells.length; x++) {
      for (let y = x + 1; y < cells.length; y++) {
        sets[cells[x]].add(cells[y]);
        sets[cells[y]].add(cells[x]);
      }
    }
  }
  return sets.map((s) => [...s].sort((p, q) => p - q));
}

// `keepLabels` — with mergeFragments': when > 0, a flip that would EXTINGUISH a label (its last
// cell) is skipped while the distinct count is at or below it — a tiny reseeded k-means cluster
// must survive smoothing when the user explicitly requested that many zones.
function majoritySmooth(
  labels: number[],
  adj: number[][],
  passes = 2,
  keepLabels = 0,
): number[] {
  let cur = labels.slice();
  for (let p = 0; p < passes; p++) {
    const next = cur.slice();
    let remaining: Map<number, number> | null = null;
    if (keepLabels > 0) {
      remaining = new Map();
      for (const l of cur) remaining.set(l, (remaining.get(l) ?? 0) + 1);
    }
    for (let i = 0; i < cur.length; i++) {
      const neigh = adj[i];
      if (neigh.length < 2) continue; // only fully isolated cells keep their label unconditionally
      const counts = new Map<number, number>();
      for (const j of neigh) counts.set(cur[j], (counts.get(cur[j]) ?? 0) + 1);
      let bestLabel = cur[i];
      let bestCount = 0;
      for (const [l, c] of counts) {
        if (c > bestCount) {
          bestCount = c;
          bestLabel = l;
        }
      }
      if (bestLabel !== cur[i] && bestCount > neigh.length / 2) {
        if (remaining) {
          const left = remaining.get(cur[i]) ?? 0;
          if (left <= 1 && remaining.size <= keepLabels) continue; // extinguishing flip — the request wins
          if (left - 1 > 0) remaining.set(cur[i], left - 1);
          else remaining.delete(cur[i]);
          remaining.set(bestLabel, (remaining.get(bestLabel) ?? 0) + 1);
        }
        next[i] = bestLabel;
      }
    }
    cur = next;
  }
  return cur;
}

// always ≥ any one of its patches, so the label test can never fire alone) — so other callers are
// unaffected. `keepLabels` — the REQUESTED zone count wins over the acreage floor: when > 0, a
// label's LAST remaining patch is never absorbed while the distinct-label count is at or below it —
// so an explicitly requested k survives the cleanup even when the zones it implies run under the
// floor.
export function mergeFragments(
  labels: number[],
  adj: number[][],
  minCells: number,
  minPartCells: number = minCells,
  keepLabels = 0,
): number[] {
  const cur = labels.slice();
  for (let pass = 0; pass < 6; pass++) {
    // per-label totals for THIS pass (recomputed each pass — merges move cells between labels)
    const labelTotal = new Map<number, number>();
    for (const l of cur) labelTotal.set(l, (labelTotal.get(l) ?? 0) + 1);
    // LIVE per-label counts — absorbs within this pass move cells between labels, and the keepLabels
    // guard must judge "is this the label's last patch" against the current state, not the snapshot
    const remaining = new Map(labelTotal);
    const comp = new Array<number>(cur.length).fill(-1);
    const compCells: number[][] = [];
    for (let i = 0; i < cur.length; i++) {
      if (comp[i] !== -1) continue;
      const queue = [i];
      comp[i] = compCells.length;
      const members: number[] = [];
      while (queue.length) {
        const u = queue.pop()!;
        members.push(u);
        for (const v of adj[u]) {
          if (comp[v] === -1 && cur[v] === cur[i]) {
            comp[v] = comp[i];
            queue.push(v);
          }
        }
      }
      compCells.push(members);
    }
    let merged = false;
    for (const members of compCells) {
      const lab = cur[members[0]];
      // KEEP when the patch is big enough AND its label is a viable zone overall; absorb otherwise
      if (
        members.length >= minPartCells &&
        (labelTotal.get(lab) ?? 0) >= minCells
      )
        continue;
      // the request wins over the floor: never absorb a label's LAST patch at/below the requested count
      if (
        keepLabels > 0 &&
        (remaining.get(lab) ?? 0) <= members.length &&
        remaining.size <= keepLabels
      )
        continue;
      const border = new Map<number, number>();
      for (const u of members) {
        for (const v of adj[u]) {
          if (cur[v] !== cur[u])
            border.set(cur[v], (border.get(cur[v]) ?? 0) + 1);
        }
      }
      let bestLabel = -1;
      let bestCount = 0;
      for (const [l, c] of border) {
        if (c > bestCount) {
          bestCount = c;
          bestLabel = l;
        }
      }
      if (bestLabel >= 0) {
        for (const u of members) cur[u] = bestLabel;
        const left = (remaining.get(lab) ?? 0) - members.length;
        if (left > 0) remaining.set(lab, left);
        else remaining.delete(lab);
        remaining.set(
          bestLabel,
          (remaining.get(bestLabel) ?? 0) + members.length,
        );
        merged = true;
      }
    }
    if (!merged) break;
  }
  return cur;
}

// dissolve same-label cells into polygons by EDGE CANCELLATION: interior edges (shared by
// two same-label cells, traversed in opposite directions) cancel; what remains chains into
// boundary rings. CCW rings = outers, CW = holes (assigned to the containing outer).
export function dissolveLabel(
  ringsPerCell: Ring[][],
  memberIdx: number[],
): GeoJSON.MultiPolygon {
  const key = (p: number[]) => `${p[0].toFixed(9)},${p[1].toFixed(9)}`;
  const edges = new Map<string, { a: number[]; b: number[] }>();
  for (const i of memberIdx) {
    for (const r of ringsPerCell[i]) {
      for (let j = 0; j < r.length; j++) {
        const a = r[j];
        const b = r[(j + 1) % r.length];
        const rev = `${key(b)}|${key(a)}`;
        if (edges.has(rev)) edges.delete(rev);
        else edges.set(`${key(a)}|${key(b)}`, { a, b });
      }
    }
  }
  // chain into rings
  const byStart = new Map<string, { a: number[]; b: number[] }[]>();
  for (const e of edges.values()) {
    const k = key(e.a);
    const arr = byStart.get(k);
    if (arr) arr.push(e);
    else byStart.set(k, [e]);
  }
  const outRings: Ring[] = [];
  for (const e0 of edges.values()) {
    const startArr = byStart.get(key(e0.a));
    if (!startArr || !startArr.includes(e0)) continue;
    const ring: Ring = [];
    let e: { a: number[]; b: number[] } | undefined = e0;
    while (e) {
      ring.push(e.a);
      const arr = byStart.get(key(e.a));
      if (arr) {
        const idx = arr.indexOf(e);
        if (idx >= 0) arr.splice(idx, 1);
      }
      const nextArr = byStart.get(key(e.b));
      e = nextArr?.length ? nextArr[0] : undefined;
      if (ring.length > 200_000) break; // safety valve
    }
    if (ring.length >= 3) outRings.push(ring);
  }
  // classify: CCW outer / CW hole
  const signedArea = (r: Ring) => {
    let a = 0;
    for (let i = 0, j = r.length - 1; i < r.length; j = i++)
      a += r[j][0] * r[i][1] - r[i][0] * r[j][1];
    return a / 2;
  };
  const inRing = (pt: number[], r: Ring) => {
    let inside = false;
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const [xi, yi] = r[i];
      const [xj, yj] = r[j];
      if (
        yi > pt[1] !== yj > pt[1] &&
        pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi
      )
        inside = !inside;
    }
    return inside;
  };
  const outers: { ring: Ring; area: number; holes: Ring[] }[] = [];
  const holes: Ring[] = [];
  for (const r of outRings) {
    const a = signedArea(r);
    if (a >= 0) outers.push({ ring: r, area: a, holes: [] });
    else holes.push(r);
  }
  outers.sort((x, y) => x.area - y.area); // smallest first → holes land in their tightest container
  for (const h of holes) {
    const owner = outers.find((o) => inRing(h[0], o.ring));
    (owner ?? outers[outers.length - 1])?.holes.push(h);
  }
  const closed = (r: Ring) => [...r, r[0]];
  return {
    type: "MultiPolygon",
    coordinates: outers.map((o) => [
      closed(o.ring),
      ...o.holes.map(closed),
    ]) as GeoJSON.Position[][][],
  };
}

// ---- dissolve a per-cell label array into zone polygons (SHARED by designZones + the interactive editor) ----
// Each distinct label (≥0; negative = unassigned gap, skipped) dissolves into one zone feature. Features are
// sorted largest-area-first; each gets a MEDOID-snapped label anchor (nearest member cell to the largest
// patch's centroid — keeps the label inside concave/annular zones). `zone` property = the label value, unless
// `renumberByArea` renumbers to 1..k in area order (designZones' initial pass). `geom` may be passed
// precomputed so the interactive re-dissolve after a paint stroke skips the O(n) rings/adjacency rebuild.
export interface ZoneGeom {
  ringsPerCell: Ring[][];
  centroids: { lng: number; lat: number }[];
  adj: number[][];
}
export function buildZoneGeom(
  geoms: { type: string; coordinates: unknown }[],
): ZoneGeom {
  const ringsPerCell = geoms.map(outerRingsOf);
  const centroids = ringsPerCell.map((rs) => centroidOfRing(rs[0]));
  return { ringsPerCell, centroids, adj: buildAdjacency(ringsPerCell) };
}
// CONSUME the SERVED relative-zones design as the editor's starting state instead of re-deriving it
// client-side. Assigns each cell to the served zone polygon that CONTAINS its centroid, so
// Customize opens on compute's EXACT design (same shapes, same count) rather than an approximation
// of it — the same trick as `assignCellsToServedZones` for High-Res points.
export function labelsFromServedZones(
  centroids: { lng: number; lat: number }[],
  zonesFc: GeoJSON.FeatureCollection | null | undefined,
): number[] | null {
  const feats = zonesFc?.type === "FeatureCollection" ? zonesFc.features : null;
  if (!feats || feats.length < 2 || !centroids.length) return null;
  const nOf = (f: GeoJSON.Feature): number => {
    const p = f.properties ?? {};
    const raw =
      typeof p.zone_number === "number"
        ? p.zone_number
        : typeof p.zone === "number"
          ? p.zone
          : null;
    return raw == null ? Number.NaN : raw;
  };
  // rank the served zone numbers so labels are dense 0..k-1 regardless of the server's numbering base
  const nums = feats.map(nOf);
  const ranked = [...new Set(nums.filter((v) => Number.isFinite(v)))].sort(
    (a, b) => a - b,
  );
  const zones = feats.map((f, idx) => {
    const polys = polygonsOf(f.geometry);
    let w = Infinity,
      s = Infinity,
      e = -Infinity,
      n = -Infinity;
    for (const rings of polys)
      for (const [x, y] of rings[0] ?? []) {
        if (x < w) w = x;
        if (x > e) e = x;
        if (y < s) s = y;
        if (y > n) n = y;
      }
    const r = ranked.indexOf(nums[idx]);
    return { label: (r >= 0 ? r : idx) + 1, polys, w, s, e, n };
  });
  if (!zones.some((z) => z.polys.length)) return null;

  const labels = new Array<number>(centroids.length).fill(-1);
  for (let i = 0; i < centroids.length; i++) {
    const p = centroids[i];
    for (const z of zones) {
      if (p.lng < z.w || p.lng > z.e || p.lat < z.s || p.lat > z.n) continue;
      if (pointInPolygons(p, z.polys)) {
        labels[i] = z.label;
        break;
      }
    }
  }
  const assigned = labels.reduce((n, l) => (l >= 0 ? n + 1 : n), 0);
  // a served design that covers almost nothing is a mismatch (wrong boundary version / stale FC) — don't use it
  if (assigned < centroids.length * 0.5) return null;
  if (assigned < centroids.length) {
    for (let i = 0; i < labels.length; i++) {
      if (labels[i] >= 0) continue;
      let best = -1,
        bestD = Infinity;
      for (let j = 0; j < labels.length; j++) {
        if (labels[j] < 0) continue;
        const dx = centroids[j].lng - centroids[i].lng;
        const dy = centroids[j].lat - centroids[i].lat;
        const dd = dx * dx + dy * dy;
        if (dd < bestD) {
          bestD = dd;
          best = j;
        }
      }
      if (best >= 0) labels[i] = labels[best];
    }
  }
  return labels;
}

export function zonesFromLabels(
  geoms: { type: string; coordinates: unknown }[],
  labels: number[],
  opts?: { renumberByArea?: boolean; geom?: ZoneGeom },
): {
  fc: GeoJSON.FeatureCollection;
  labelPoints: { zone: number; lng: number; lat: number }[];
  areaOrder: number[];
} {
  const { ringsPerCell, centroids, adj } = opts?.geom ?? buildZoneGeom(geoms);
  const byLabel = new Map<number, number[]>();
  labels.forEach((l, i) => {
    if (l < 0) return;
    const arr = byLabel.get(l);
    if (arr) arr.push(i);
    else byLabel.set(l, [i]);
  });
  const zones = [...byLabel.entries()].map(([label, members]) => {
    const geom = dissolveLabel(ringsPerCell, members);
    const area = polygonAreaM2(geom) / 4046.86;
    // largest same-label patch → medoid anchor
    const compSeen = new Set<number>();
    let bestPatch: number[] = [];
    for (const s of members) {
      if (compSeen.has(s)) continue;
      const queue = [s];
      compSeen.add(s);
      const patch: number[] = [];
      while (queue.length) {
        const u = queue.pop()!;
        patch.push(u);
        for (const v of adj[u]) {
          if (!compSeen.has(v) && labels[v] === label) {
            compSeen.add(v);
            queue.push(v);
          }
        }
      }
      if (patch.length > bestPatch.length) bestPatch = patch;
    }
    // label anchor: the DEEPEST-INTERIOR cell of the largest patch (BFS depth from the patch
    // boundary discrete pole of inaccessibility; ties toward the patch mean), matching the behavior
    // dissolve.
    const interiorDegree = adj.reduce((m2, a2) => Math.max(m2, a2.length), 0);
    const patchSet = new Set(bestPatch);
    const depth = new Map<number, number>();
    const bfs: number[] = [];
    for (const i of bestPatch) {
      if (
        adj[i].length < interiorDegree ||
        adj[i].some((v) => !patchSet.has(v))
      ) {
        depth.set(i, 0);
        bfs.push(i);
      }
    }
    for (let h = 0; h < bfs.length; h++) {
      const u = bfs[h];
      const du = depth.get(u)!;
      for (const v of adj[u])
        if (patchSet.has(v) && !depth.has(v)) {
          depth.set(v, du + 1);
          bfs.push(v);
        }
    }
    const mean = {
      lng:
        bestPatch.reduce((s, i) => s + centroids[i].lng, 0) / bestPatch.length,
      lat:
        bestPatch.reduce((s, i) => s + centroids[i].lat, 0) / bestPatch.length,
    };
    let anchorIdx = bestPatch[0];
    let bestScore = -Infinity;
    for (const i of bestPatch) {
      const dep = depth.get(i) ?? 0;
      const dd =
        (centroids[i].lng - mean.lng) ** 2 + (centroids[i].lat - mean.lat) ** 2;
      const score = dep * 1e9 - dd;
      if (score > bestScore) {
        bestScore = score;
        anchorIdx = i;
      }
    }
    return { label, geom, area, anchor: centroids[anchorIdx] };
  });
  zones.sort((x, y) => y.area - x.area);
  const zoneNum = (z: { label: number }, i: number) =>
    opts?.renumberByArea ? i + 1 : z.label;
  return {
    fc: {
      type: "FeatureCollection",
      features: zones.map((z, i) => ({
        type: "Feature",
        properties: {
          zone: zoneNum(z, i),
          zone_name: String(zoneNum(z, i)),
          area_acres: Math.round(z.area * 10) / 10,
        },
        geometry: z.geom,
      })),
    },
    labelPoints: zones.map((z, i) => ({
      zone: zoneNum(z, i),
      lng: z.anchor.lng,
      lat: z.anchor.lat,
    })),
    areaOrder: zones.map((z) => z.label),
  };
}

// The cluster step the server also runs. Shared vectors check the labels.
export function assignClusters(
  rows: number[][],
  k: number,
  seed: number,
): number[] {
  return kmeans(rows, k, seed).assign;
}

// ---- the design -------------------------------------------------------------------------
export function designZones(a: ZoneDesignArgs): ZoneDesignResult {
  const n = a.geoms.length;
  if (n < 20)
    throw new Error(
      "Not enough field data to zone (the covariate stack looks empty).",
    );
  const X = whitenFeatures(a.features, n, a.weights);
  if (!X)
    throw new Error(
      "No usable covariates on this field yet — zones need the field data (terrain/imagery) to be collected first.",
    );

  // scan k for the elbow (k=1 baseline = total variance)
  const base = kmeans(X, 1, a.seed).withinSS;
  const evByK: Record<number, number> = {};
  const assignByK: Record<number, number[]> = {};
  for (let k = K_MIN; k <= K_MAX; k++) {
    const r = kmeans(X, k, a.seed + k);
    evByK[k] = base > 0 ? 1 - r.withinSS / base : 0;
    assignByK[k] = r.assign;
  }
  let autoK = K_MIN;
  for (let k = K_MIN + 1; k <= K_MAX; k++) {
    if (evByK[k] - evByK[k - 1] >= MIN_EV_GAIN) autoK = k;
    else break;
  }
  const ringsPerCell = a.geoms.map(outerRingsOf);
  const centroids = ringsPerCell.map((rs) => centroidOfRing(rs[0])); // first part anchors adjacency
  const adj = buildAdjacency(ringsPerCell);
  const cellAcres =
    polygonAreaM2({
      type: "Polygon",
      coordinates: [[...ringsPerCell[0][0], ringsPerCell[0][0][0]]],
    }) / 4046.86;
  const minCells = Math.max(
    3,
    Math.round((a.minZoneAcres ?? 1) / Math.max(cellAcres, 1e-6)),
  );
  // A zone may be MULTI-PART, but a part must still be farmable — parity with compute's
  // `MIN_ZONE_PART_ACRES`. Without a part floor a "part" can be a single 0.0158-ac cell and the
  // design degenerates into speckle (compute measured 31–95 disconnected specks per label on
  // at every k); at 1 ac it is 1–3.
  const minPartCells = Math.max(
    3,
    Math.round(MIN_ZONE_PART_ACRES / Math.max(cellAcres, 1e-6)),
  );

  // Acres = the caller's STORED boundary figure; the cell-sum is only the no-figure fallback.
  const fieldAcres = a.boundaryAcres ?? cellAcres * n;
  autoK = Math.min(
    MAX_ZONES,
    Math.max(autoK, Math.floor(fieldAcres / TARGET_ACRES_PER_ZONE)),
  );
  // an EXPLICIT request may exceed the elbow scan range — allow up to MAX_ZONES; assignments/EV for
  // counts beyond the scan are computed on demand (assignAt).
  const k = Math.min(Math.max(a.k ?? autoK, K_MIN), MAX_ZONES);

  // Homogeneity (ev) is ALWAYS measured in pure covariate space, so the trade is visible, never
  // hidden.
  const ky = METRES_PER_DEGREE_LAT;
  const kx =
    METRES_PER_DEGREE_LNG *
    Math.cos(((centroids[0]?.lat ?? 0) * Math.PI) / 180);
  const mx = centroids.reduce((s, c) => s + c.lng, 0) / n;
  const my = centroids.reduce((s, c) => s + c.lat, 0) / n;
  const geoRaw = centroids.map((c) => [(c.lng - mx) * kx, (c.lat - my) * ky]);
  const geoVarRaw = geoRaw.reduce((s, g) => s + g[0] ** 2 + g[1] ** 2, 0) / n;
  const d = X[0].length;
  // ONE design pass at a target count, with a given floor + protection level base k-means → cleanup
  // → the spatial-share ladder while short of the target: geography weighted progressively into the
  // features so spatially coherent zones survive the cleanup.
  const SPATIAL_SHARES = [0, 0.15, 0.35, 0.6];
  const assignAt = (kt: number): number[] => {
    if (assignByK[kt] == null) {
      const r = kmeans(X, kt, a.seed + kt);
      assignByK[kt] = r.assign;
      evByK[kt] = base > 0 ? 1 - r.withinSS / base : 0;
    }
    return assignByK[kt];
  };
  const runDesign = (
    kt: number,
    mc: number,
    mpc: number,
    protect: number,
  ): number[] => {
    const cleanup = (assign: number[]) =>
      mergeFragments(
        majoritySmooth(assign, adj, 2, protect),
        adj,
        mc,
        mpc,
        protect,
      );
    let out = cleanup(assignAt(kt));
    let bestDistinct = new Set(out).size;
    for (let wi = 1; wi < SPATIAL_SHARES.length && bestDistinct < kt; wi++) {
      const w = SPATIAL_SHARES[wi];
      // whitened features carry total variance ≈ d; scale geo so its block holds a `w` share
      const gs = geoVarRaw > 0 ? Math.sqrt(((w / (1 - w)) * d) / geoVarRaw) : 0;
      const Xw = X.map((row, i) => [
        ...row,
        geoRaw[i][0] * gs,
        geoRaw[i][1] * gs,
      ]);
      const candidate = cleanup(kmeans(Xw, kt, a.seed + kt + wi * 101).assign);
      const cd = new Set(candidate).size;
      if (cd > bestDistinct) {
        out = candidate;
        bestDistinct = cd;
      }
    }
    return out;
  };
  const distinctOf = (ls: number[]) => {
    const seen = new Set<number>();
    for (const l of ls) if (l >= 0) seen.add(l);
    return seen.size;
  };

  let labels: number[];
  let autoDeliveredK: number | undefined;
  if (a.k == null) {
    // AUTO-K — the elbow's own pick with floor-wins cleanup: the served-AutoMap parity path, byte-unchanged.
    labels = runDesign(k, minCells, minPartCells, 0);
    autoDeliveredK = distinctOf(labels);
  } else if (a.protectK) {
    // PURE-CATEGORICAL BASE (the one protected caller, zoneEditStore's steering branch): classes are
    // IDENTITIES, not statistical clusters — a sub-floor soil type must survive as its own zone. The floor
    // shrinks to what k zones can average and the cleanup keeps every label's last cell/patch alive.
    // Probing can't serve this caller: k-means on identical one-hot vectors caps at the class count.
    const mc = Math.max(3, Math.min(minCells, Math.floor(n / (2 * k))));
    labels = runDesign(k, mc, Math.min(minPartCells, mc), k);
  } else {
    const autoDesign = runDesign(autoK, minCells, minPartCells, 0);
    autoDeliveredK = distinctOf(autoDesign);
    if (autoDeliveredK === k) {
      labels = autoDesign;
    } else {
      // PASS 0 — the ORIGINAL process at the requested k, verbatim.
      labels = runDesign(k, minCells, minPartCells, 0);
      if (distinctOf(labels) !== k) {
        // PROBE upward — re-run the original process at higher targets, the floor adapted to the PROBED
        // count (min of 4 ac and half the average zone size): zones may run under 4 ac at high k, but never
        // a speck — cleanup still WINS, nothing is protected. First exact delivery ends the sweep.
        let over: number[] | null = null;
        let overD = Infinity;
        let under = labels;
        let underD = distinctOf(labels);
        for (let kp = k; kp <= Math.min(k + PROBE_SPAN, MAX_ZONES); kp++) {
          const mc = Math.max(3, Math.min(minCells, Math.floor(n / (2 * kp))));
          const cand = runDesign(kp, mc, Math.min(minPartCells, mc), 0);
          const cd = distinctOf(cand);
          if (cd === k) {
            labels = cand;
            break;
          }
          if (cd > k && cd < overD) {
            over = cand;
            overD = cd;
          } else if (cd < k && cd > underD) {
            under = cand;
            underD = cd;
          }
        }
        // EXACTNESS — always deliver the requested number: an overshoot walks DOWN via the
        // most-border merge; a stubborn undershoot walks UP via the geographic splitter. Both
        // deterministic both produce readable zones — split pieces are 30–70% of the zone they split
        // merges only remove.
        if (distinctOf(labels) !== k) {
          labels =
            over != null
              ? mergeToCount(over, adj, k)
              : splitToCount(under, centroids, adj, k);
        }
      }
    }
  }

  // explained variance of the FINAL partition (post smoothing/merge — what actually gets
  // saved), not the raw k-means figure: cleanup can only lose homogeneity, and a merged-
  // away label must not keep its pre-merge EV%.
  const finalLabels = [...new Set(labels)];
  let withinFinal = 0;
  for (const l of finalLabels) {
    const members = labels.reduce<number[]>((acc, ll, i) => {
      if (ll === l) acc.push(i);
      return acc;
    }, []);
    const mean = new Array<number>(d).fill(0);
    for (const i of members) for (let c = 0; c < d; c++) mean[c] += X[i][c];
    for (let c = 0; c < d; c++) mean[c] /= members.length;
    for (const i of members)
      for (let c = 0; c < d; c++) withinFinal += (X[i][c] - mean[c]) ** 2;
  }
  const evFinal = base > 0 ? 1 - withinFinal / base : 0;

  // dissolve per surviving label; zones numbered by area (largest = 1). Reuse the precomputed geometry
  // so we don't rebuild rings/adjacency, and renumber to area order.
  const zg: ZoneGeom = { ringsPerCell, centroids, adj };
  const dissolved = zonesFromLabels(a.geoms, labels, {
    renumberByArea: true,
    geom: zg,
  });
  // remap each cell's raw cluster label → its 1-based area rank, so the editor's per-cell `labels` match the
  // zone numbers on the map (painting "into zone 3" sets label 3). Gap cells (unlabeled) stay -1.
  const rank = new Map<number, number>(
    dissolved.areaOrder.map((l, i) => [l, i + 1]),
  );
  const editorLabels = labels.map((l) => rank.get(l) ?? -1);

  return {
    fc: dissolved.fc,
    k: dissolved.fc.features.length,
    requestedK: k,
    autoK,
    autoDeliveredK,
    evByK,
    ev: evFinal,
    labelPoints: dissolved.labelPoints,
    labels: editorLabels,
  };
}
